import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';

// ─── Constants ───────────────────────────────────────────────────────────────

const REASON_LABELS = {
  salary_increment:   'Salary Increment',
  annual_appraisal:   'Annual Appraisal',
  promotion:          'Promotion',
  disciplinary_action:'Disciplinary Action',
  others:             'Others',
};

const STATUS_LABELS = {
  pending_employee: 'Employee Pending',
  pending_manager:  'Manager Review',
  pending_approver: 'Final Approval',
  completed:        'Completed',
  rejected:         'Rejected',
  closed:           'Closed',
};

const STATUS_COLORS = {
  pending_employee: '#f39c12',
  pending_manager:  '#3498db',
  pending_approver: '#9b59b6',
  completed:        '#27ae60',
  rejected:         '#e74c3c',
  closed:           '#8a94a6',
};

// Performance-review status (distinct from the appraisal-level status
// above) — surfaced on the per-cycle review summary badge.
const REVIEW_STATUS_LABELS = {
  pending_self:     'Pending Self-Rating',
  pending_manager:  'Pending Manager Review',
  pending_approver: 'Pending Final Approval',
  approved:         'Approved',
  rejected:         'Rejected',
};

const REVIEW_STATUS_COLORS = {
  pending_self:     '#f39c12',
  pending_manager:  '#3498db',
  pending_approver: '#9b59b6',
  approved:         '#27ae60',
  rejected:         '#e74c3c',
};

const RECOMMENDATION_LABELS = {
  recommend:     'Recommend',
  not_recommend: 'Not Recommend',
  conditional:   'Conditional',
};

function userName(u) {
  if (!u) return 'Unknown';
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
}

function avg(ratings) {
  if (!ratings.length) return null;
  return (ratings.reduce((s, r) => s + (r.rating || 0), 0) / ratings.length).toFixed(2);
}

// Goals are weighted by each goal's weightage, matching the "Goals
// (Weighted Avg)" card on the review detail page — pooling them
// unweighted (like skills/values) gives a different number for the
// same review. totalWeightage must be the total across ALL goals in
// the review (not just the rated ones passed in `ratings`), since an
// unrated goal still counts toward the denominator on the detail page.
function weightedAvg(ratings, totalWeightage) {
  if (!ratings.length || !totalWeightage) return null;
  const weighted = ratings.reduce((s, r) => s + (r.weightage || 0) * (r.rating || 0), 0);
  return (weighted / totalWeightage).toFixed(2);
}

// ─── Star display ─────────────────────────────────────────────────────────────

function Stars({ value }) {
  if (!value) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>;
  return (
    <span style={{ color: '#f1c40f', fontSize: 14 }}>
      {'★'.repeat(Math.floor(value))}{'☆'.repeat(5 - Math.floor(value))}
      <span style={{ color: 'var(--text-secondary)', fontSize: 12, marginLeft: 4 }}>({value})</span>
    </span>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, labels = STATUS_LABELS, colors = STATUS_COLORS }) {
  const color = colors[status] || '#888888';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      background: color + '20',
      color,
    }}>
      {labels[status] || status}
    </span>
  );
}

// ─── Review summary for a single cycle ───────────────────────────────────────

function CycleReviewSummary({ summary }) {
  const { cycle, review, ratings, has_goals, has_skills, has_values, total_goal_weightage } = summary;
  const [expanded, setExpanded] = useState(false);

  if (!cycle) return null;
  const cycleName = `${cycle.cycle_name} (${cycle.year})`;

  if (!review) {
    return (
      <div style={{ ...summaryBox, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <strong style={{ fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{cycleName}</strong>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No review found for this cycle</span>
      </div>
    );
  }

  const selfGoals    = ratings.filter(r => r.rater_role === 'self'    && r.rating_type === 'goal');
  const manGoals     = ratings.filter(r => r.rater_role === 'manager' && r.rating_type === 'goal');
  const selfSkills   = ratings.filter(r => r.rater_role === 'self'    && r.rating_type === 'skill');
  const manSkills    = ratings.filter(r => r.rater_role === 'manager' && r.rating_type === 'skill');
  const selfValues   = ratings.filter(r => r.rater_role === 'self'    && r.rating_type === 'value');
  const manValues    = ratings.filter(r => r.rater_role === 'manager' && r.rating_type === 'value');

  // Ratings rows carry over as a draft when a stage is rejected back for
  // revision (e.g. manager rejects to employee → status returns to
  // pending_self), so the row still exists even though it isn't the
  // current, finalized submission. Only surface stars once that stage has
  // actually been (re)submitted — i.e. status has moved past it.
  const selfSubmitted    = !['pending_self', 'rejected'].includes(review.status);
  const managerSubmitted = !['pending_self', 'pending_manager', 'rejected'].includes(review.status);

  return (
    <div style={summaryBox}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <strong style={{ fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{cycleName}</strong>
          <span style={{ marginLeft: 10 }}>
            <StatusBadge status={review.status} labels={REVIEW_STATUS_LABELS} colors={REVIEW_STATUS_COLORS} />
          </span>
        </div>
        <div style={{ display: 'flex', flexShrink: 0, flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
          {has_goals && (
            <span>Goals: Self <Stars value={selfSubmitted ? weightedAvg(selfGoals, total_goal_weightage) : null} /> | Mgr <Stars value={managerSubmitted ? weightedAvg(manGoals, total_goal_weightage) : null} /></span>
          )}
          {has_skills && (
            <span>Skills: Self <Stars value={selfSubmitted ? avg(selfSkills) : null} /> | Mgr <Stars value={managerSubmitted ? avg(manSkills) : null} /></span>
          )}
          {has_values && (
            <span>Values: Self <Stars value={selfSubmitted ? avg(selfValues) : null} /> | Mgr <Stars value={managerSubmitted ? avg(manValues) : null} /></span>
          )}
          <button className="btn-link-sm" onClick={() => setExpanded(x => !x)}>
            {expanded ? 'Hide' : 'Details'}
          </button>
        </div>
      </div>

      {expanded && !selfSubmitted && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          Self review not started yet.
        </div>
      )}

      {expanded && selfSubmitted && ratings.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12 }}>
          {['goal', 'skill', 'value'].map(type => {
            const typeRatings = ratings.filter(r => r.rating_type === type);
            if (!typeRatings.length) return null;
            const label = type === 'goal' ? 'Goals' : type === 'skill' ? 'Skills' : 'Values';
            const nameField = type === 'goal' ? 'goal_name' : type === 'skill' ? 'skill_name' : 'value_name';
            const snapshotIds = [...new Set(typeRatings.map(r => r.snapshot_id))];
            return (
              <div key={type} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                {snapshotIds.map(sid => {
                  const group = typeRatings.filter(r => r.snapshot_id === sid);
                  const name = group[0]?.[nameField] || `#${sid}`;
                  const self = group.find(r => r.rater_role === 'self');
                  const mgr  = group.find(r => r.rater_role === 'manager');
                  return (
                    <div key={sid} style={{ display: 'flex', gap: 12, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ flex: 1, color: 'var(--text-primary)' }}>{name}</span>
                      <span>Self: <Stars value={selfSubmitted ? self?.rating : null} /></span>
                      <span>Manager: <Stars value={managerSubmitted ? mgr?.rating : null} /></span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Appraisal detail modal ───────────────────────────────────────────────────

export function AppraisalDetail({ appraisalId, onClose, onRefresh, me, canManage, viewOnly }) {
  const { appId } = useParams();
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Employee form state
  const [empForm, setEmpForm] = useState({
    things_went_well: '',
    could_be_better: '',
    next_term_aspirations: '',
    support_required: '',
  });

  // Manager form state
  const [mgrForm, setMgrForm] = useState({
    goals_feedback: '',
    skills_feedback: '',
    values_feedback: '',
    potential_assessment: '',
    recommendation: '',
    recommendation_notes: '',
  });

  // Final decision state
  const [finalForm, setFinalForm] = useState({ decision: '', decision_notes: '', reject_target: '' });

  // Manager reject-to-employee state
  const [showMgrReject, setShowMgrReject] = useState(false);
  const [mgrRejectReason, setMgrRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getAppraisal(appId, appraisalId);
      setData(d);
      if (d.employee_response) {
        setEmpForm({
          things_went_well:    d.employee_response.things_went_well    || '',
          could_be_better:     d.employee_response.could_be_better     || '',
          next_term_aspirations: d.employee_response.next_term_aspirations || '',
          support_required:    d.employee_response.support_required    || '',
        });
      }
      if (d.manager_response) {
        setMgrForm({
          goals_feedback:       d.manager_response.goals_feedback       || '',
          skills_feedback:      d.manager_response.skills_feedback      || '',
          values_feedback:      d.manager_response.values_feedback      || '',
          potential_assessment: d.manager_response.potential_assessment || '',
          recommendation:       d.manager_response.recommendation       || '',
          recommendation_notes: d.manager_response.recommendation_notes || '',
        });
      }
      if (d.draft_decision || d.draft_decision_notes || d.draft_reject_target) {
        setFinalForm({
          decision:      d.draft_decision       || '',
          decision_notes: d.draft_decision_notes || '',
          reject_target: d.draft_reject_target   || '',
        });
      }
    } catch (e) {
      setError('Failed to load appraisal');
    } finally {
      setLoading(false);
    }
  }, [appId, appraisalId]);

  useEffect(() => { load(); }, [load]);

  const empFormComplete = ['things_went_well', 'could_be_better', 'next_term_aspirations', 'support_required']
    .every(k => empForm[k].trim().length > 0);

  const mgrFormComplete = ['goals_feedback', 'skills_feedback', 'values_feedback', 'potential_assessment', 'recommendation_notes']
    .every(k => mgrForm[k].trim().length > 0);

  const submitEmployeeResponse = async () => {
    if (!empFormComplete) { setError('Please fill in all fields before submitting'); return; }
    if (!window.confirm('Submit your reflection for manager review?')) return;
    setSaving(true); setError('');
    try {
      await api.submitEmployeeResponse(appId, appraisalId, empForm);
      onRefresh(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit');
    } finally { setSaving(false); }
  };

  const saveEmployeeDraft = async () => {
    setDraftSaving(true); setDraftSaved(false); setError('');
    try {
      await api.saveEmployeeResponseDraft(appId, appraisalId, empForm);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save draft');
    } finally { setDraftSaving(false); }
  };

  const submitManagerResponse = async () => {
    if (!mgrFormComplete) { setError('Please fill in all fields before submitting'); return; }
    if (!window.confirm('Submit your manager feedback for this appraisal?')) return;
    setSaving(true); setError('');
    try {
      await api.submitManagerResponse(appId, appraisalId, mgrForm);
      onRefresh(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit');
    } finally { setSaving(false); }
  };

  const saveManagerDraft = async () => {
    setDraftSaving(true); setDraftSaved(false); setError('');
    try {
      await api.saveManagerResponseDraft(appId, appraisalId, mgrForm);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save draft');
    } finally { setDraftSaving(false); }
  };

  const submitManagerReject = async () => {
    if (!mgrRejectReason.trim()) { setError('Please explain why this is being sent back'); return; }
    if (!window.confirm('Send this appraisal back to the employee for revision?')) return;
    setSaving(true); setError('');
    try {
      await api.rejectAppraisalToEmployee(appId, appraisalId, { rejection_reason: mgrRejectReason });
      setShowMgrReject(false); setMgrRejectReason('');
      onRefresh(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send back');
    } finally { setSaving(false); }
  };

  const submitFinalDecision = async () => {
    if (!finalForm.decision) { setError('Please select a decision'); return; }
    if (finalForm.decision === 'rejected' && !finalForm.reject_target) {
      setError('Please choose who this should be sent back to');
      return;
    }
    if (!finalForm.decision_notes.trim()) { setError('Decision notes are required'); return; }
    const confirmMsg = finalForm.decision === 'rejected'
      ? `Send this appraisal back to the ${finalForm.reject_target === 'manager' ? 'manager' : 'employee'} for revision?`
      : 'Approve this appraisal? This finalizes the decision.';
    if (!window.confirm(confirmMsg)) return;
    setSaving(true); setError('');
    try {
      await api.submitFinalDecision(appId, appraisalId, finalForm);
      onRefresh(); onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit');
    } finally { setSaving(false); }
  };

  const saveFinalDecisionDraft = async () => {
    setDraftSaving(true); setDraftSaved(false); setError('');
    try {
      await api.saveFinalDecisionDraft(appId, appraisalId, finalForm);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save draft');
    } finally { setDraftSaving(false); }
  };

  if (loading) return (
    <div style={modalOverlay}>
      <div style={modalBox}><div className="loading">Loading appraisal…</div></div>
    </div>
  );

  if (!data) return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        <p style={{ color: '#e74c3c' }}>{error || 'Not found'}</p>
        <button className="btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  );

  const a = data;
  const isEmployee = a.is_employee;
  const isManager  = a.is_manager;
  const isFinalApprover = a.is_final_approver;

  const tabs = ['overview', 'timeline', 'reviews'];
  if (isEmployee && a.status === 'pending_employee') tabs.push('my_response');
  if (isManager  && a.status === 'pending_manager')  tabs.push('manager_feedback');
  // viewOnly: this same person is also the final approver, but got here via
  // their manager-side "sent for approval" entry — the actual final-decision
  // action lives only in their separate approver-side "awaiting decision"
  // entry, so suppress the form here to avoid exposing it from both places.
  const showFinalDecisionForm = !viewOnly && isFinalApprover && a.status === 'pending_approver';
  if (showFinalDecisionForm) tabs.push('final_decision');
  // Read-only view for the final decision (kept — approval/rejection outcome, not covered in the
  // timeline detail). Suppressed when the active decision form above is already showing — otherwise
  // a stale decision from an earlier reject-and-resubmit round duplicates the "Final Decision" tab.
  if (a.final_decision && !showFinalDecisionForm) tabs.push('final_view');

  const tabLabels = {
    overview:        'Overview',
    timeline:        'Timeline',
    reviews:         'Review Ratings',
    my_response:     'My Reflection',
    manager_feedback:'Manager Feedback',
    final_decision:  'Final Decision',
    final_view:      'Final Decision',
  };

  return (
    <div style={modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div data-dropdown-bounds style={{ ...modalBox, maxWidth: 1080, maxHeight: '92vh', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>Appraisal — {userName(a.employee)}</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {a.role?.title} · {REASON_LABELS[a.reason] || a.reason}
              {a.reason_notes ? ` — ${a.reason_notes}` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatusBadge status={a.status} />
            <button className="btn-link-sm" onClick={onClose} style={{ fontSize: 18, lineHeight: 1 }}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              style={{
                background: 'none', border: 'none', padding: '8px 14px', cursor: 'pointer',
                fontSize: 13, fontWeight: activeTab === t ? 700 : 400,
                color: activeTab === t ? 'var(--brand)' : 'var(--text-secondary)',
                borderBottom: activeTab === t ? '2px solid var(--brand)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {tabLabels[t]}
            </button>
          ))}
        </div>

        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

        {a.status === 'closed' && (
          <div style={{
            background: '#8a94a615', border: '1px solid #8a94a640', borderRadius: 8,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)',
          }}>
            This appraisal was closed{a.closed_at ? ` on ${formatDate(a.closed_at)}` : ''} and can no longer be edited.
            {a.close_reason && <> Reason: <em>{a.close_reason}</em></>}
          </div>
        )}

        {/* "Sent back by your Manager" is meaningless read back to the
            manager who did the sending — once the employee resubmits and
            it's their turn again, showing them a banner about their own
            past action is just confusing. Only the final-approver-sourced
            reject (someone else's action) is worth surfacing to the manager. */}
        {a.rejection_reason && !(isManager && a.status === 'pending_manager' && a.rejected_by_stage === 'manager') && (
          <div style={{
            background: '#e74c3c15', border: '1px solid #e74c3c40', borderRadius: 8,
            padding: '10px 14px', marginBottom: 16, fontSize: 13,
          }}>
            <div style={{ fontWeight: 700, color: '#e74c3c', marginBottom: 2 }}>
              {(a.status === 'pending_employee' || a.status === 'pending_manager') ? 'Sent back' : 'Previously sent back'} by{' '}
              {a.rejected_by_stage === 'final_approver' ? 'the Final Approver' : 'your Manager'}
              {a.status === 'pending_employee' ? ' — please edit and resubmit' : ''}
              {a.status === 'pending_manager' ? ' — please review again' : ''}
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>{a.rejection_reason}</div>
          </div>
        )}

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <InfoRow label="Employee" value={`${userName(a.employee)} (${a.employee?.email || ''})`} />
              <InfoRow label="Role" value={a.role?.title} />
              <InfoRow label="Reason" value={REASON_LABELS[a.reason]} />
              <InfoRow label="Triggered By" value={userName(a.triggered_by)} />
              <InfoRow label="Manager" value={a.manager ? userName(a.manager) : '—'} />
              <InfoRow label="Final Approver" value={a.final_approvers?.length ? a.final_approvers.map(userName).join(', ') : '—'} />
              <InfoRow label="Status" value={<StatusBadge status={a.status} />} />
              <InfoRow label="Review Periods" value={a.included_cycles?.map(c => `${c.cycle_name} (${c.year})`).join(', ') || '—'} />
            </div>

            {/* Workflow progress */}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>Workflow Progress</div>
              <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                {[
                  { key: 'pending_employee', label: 'Employee',       done: ['pending_manager','pending_approver','completed','rejected'].includes(a.status) },
                  { key: 'pending_manager',  label: 'Manager Review', done: ['pending_approver','completed','rejected'].includes(a.status) },
                  { key: 'pending_approver', label: 'Final Approval', done: ['completed','rejected'].includes(a.status) },
                  { key: 'done',             label: a.status === 'rejected' ? 'Rejected' : 'Completed', done: ['completed','rejected'].includes(a.status) },
                ].map((step, i) => (
                  <React.Fragment key={step.key}>
                    {i > 0 && <div style={{ flex: 1, height: 2, background: step.done ? 'var(--pos)' : 'var(--border)', minWidth: 24 }} />}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: step.done ? 'var(--pos)' : (a.status === step.key ? 'var(--warn)' : 'var(--border)'),
                        color: step.done || a.status === step.key ? '#fff' : 'var(--text-muted)',
                        fontSize: 13, fontWeight: 700, margin: '0 auto 4px',
                      }}>
                        {step.done ? '✓' : i + 1}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{step.label}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Summary of responses if completed — a draft-only response (is_draft)
                is still in progress, not a submitted outcome, so it's excluded here. */}
            {a.employee_response && !a.employee_response.is_draft && (
              <div style={{ ...sectionCard, marginTop: 20 }}>
                <div style={sectionHead}>Employee Reflection (submitted)</div>
                <ResponseDisplay response={a.employee_response} type="employee" />
              </div>
            )}
            {a.manager_response && !a.manager_response.is_draft && (
              <div style={{ ...sectionCard, marginTop: 12 }}>
                <div style={sectionHead}>Manager Feedback (submitted)</div>
                <ResponseDisplay response={a.manager_response} type="manager" />
              </div>
            )}
            {a.final_decision && (
              <div style={{ ...sectionCard, marginTop: 12, borderColor: a.final_decision.decision === 'approved' ? '#27ae60' : '#e74c3c' }}>
                <div style={sectionHead}>Final Decision</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    padding: '4px 16px', borderRadius: 20, fontWeight: 700, fontSize: 13,
                    background: a.final_decision.decision === 'approved' ? '#27ae6020' : '#e74c3c20',
                    color: a.final_decision.decision === 'approved' ? '#27ae60' : '#e74c3c',
                  }}>
                    {a.final_decision.decision === 'approved' ? 'APPROVED' : 'REJECTED'}
                  </span>
                  {a.final_decision.decision_notes && (
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{a.final_decision.decision_notes}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Timeline tab */}
        {activeTab === 'timeline' && <AppraisalTimeline a={a} />}

        {/* Review ratings tab */}
        {activeTab === 'reviews' && (
          <div>
            {(!a.review_summaries || !a.review_summaries.length) ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No review periods included.</p>
            ) : (
              a.review_summaries.map((s, i) => (
                <CycleReviewSummary key={i} summary={s} />
              ))
            )}
          </div>
        )}

        {/* Employee self-reflection form */}
        {activeTab === 'my_response' && isEmployee && a.status === 'pending_employee' && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Reflect on your performance over the included review periods. Be honest and constructive.
            </p>
            {[
              { key: 'things_went_well',      label: 'Things That Went Well *',  hint: 'Achievements, successes, and positive contributions.' },
              { key: 'could_be_better',        label: 'Could Be Better *',        hint: 'Areas where you feel you could have done more or differently.' },
              { key: 'next_term_aspirations',  label: 'Next Term Aspirations *',  hint: 'Your goals and growth ambitions for the next period.' },
              { key: 'support_required',       label: 'Support Required *',       hint: 'Resources, mentoring, or structural support you need.' },
            ].map(({ key, label, hint }) => (
              <div key={key} style={{ marginBottom: 18 }}>
                <label style={formLabel}>{label}</label>
                <p style={formHint}>{hint}</p>
                <textarea
                  className="form-input"
                  rows={4}
                  maxLength={10000}
                  value={empForm[key]}
                  onChange={e => setEmpForm(f => ({ ...f, [key]: e.target.value.slice(0, 10000) }))}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className="btn-secondary" onClick={saveEmployeeDraft} disabled={saving || draftSaving}>
                {draftSaving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-primary" onClick={submitEmployeeResponse} disabled={saving || !empFormComplete}>
                {saving ? 'Submitting…' : 'Submit My Reflection'}
              </button>
              {draftSaved && <span style={{ fontSize: 12, color: 'var(--pos)', fontWeight: 600 }}>Draft saved</span>}
            </div>
          </div>
        )}

        {/* Manager feedback form */}
        {activeTab === 'manager_feedback' && isManager && a.status === 'pending_manager' && (
          <div>
            {a.employee_response && (
              <div style={{ ...sectionCard, marginBottom: 20, background: 'var(--bg-muted)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--text-primary)' }}>Employee's Reflection</div>
                <ResponseDisplay response={a.employee_response} type="employee" />
              </div>
            )}

            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Provide your assessment of {userName(a.employee)}'s performance.
            </p>

            {[
              { key: 'goals_feedback',       label: 'Goals Assessment *',   hint: 'Evaluate performance against set goals and targets.' },
              { key: 'skills_feedback',       label: 'Skills Assessment *',  hint: 'Assess demonstrated skills and competencies.' },
              { key: 'values_feedback',       label: 'Values Assessment *',  hint: 'Evaluate alignment with organisational values and culture.' },
              { key: 'potential_assessment',  label: 'Potential & Growth *', hint: 'Your view on this employee\'s potential and development trajectory.' },
            ].map(({ key, label, hint }) => (
              <div key={key} style={{ marginBottom: 18 }}>
                <label style={formLabel}>{label}</label>
                <p style={formHint}>{hint}</p>
                <textarea
                  className="form-input"
                  rows={3}
                  maxLength={10000}
                  value={mgrForm[key]}
                  onChange={e => setMgrForm(f => ({ ...f, [key]: e.target.value.slice(0, 10000) }))}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            ))}

            <div style={{ marginBottom: 18 }}>
              <label style={formLabel}>Recommendation</label>
              <p style={formHint}>Do you recommend this employee for {REASON_LABELS[a.reason]}?</p>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                {[
                  { value: 'recommend',     label: 'Recommend',         color: '#27ae60' },
                  { value: 'conditional',   label: 'Conditional',       color: '#f39c12' },
                  { value: 'not_recommend', label: 'Do Not Recommend',  color: '#e74c3c' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setMgrForm(f => ({ ...f, recommendation: opt.value }))}
                    style={{
                      padding: '8px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                      border: `2px solid ${mgrForm.recommendation === opt.value ? opt.color : 'var(--border)'}`,
                      background: mgrForm.recommendation === opt.value ? opt.color + '15' : 'var(--bg-input)',
                      color: mgrForm.recommendation === opt.value ? opt.color : 'var(--text-primary)',
                      fontWeight: mgrForm.recommendation === opt.value ? 700 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <label style={formLabel}>Recommendation Notes *</label>
              <textarea
                className="form-input"
                rows={2}
                maxLength={10000}
                placeholder="Notes supporting your recommendation…"
                value={mgrForm.recommendation_notes}
                onChange={e => setMgrForm(f => ({ ...f, recommendation_notes: e.target.value.slice(0, 10000) }))}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={saveManagerDraft} disabled={saving || draftSaving}>
                {draftSaving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn-primary" onClick={submitManagerResponse} disabled={saving || !mgrForm.recommendation || !mgrFormComplete}>
                {saving ? 'Submitting…' : 'Submit Manager Feedback'}
              </button>
              {draftSaved && <span style={{ fontSize: 12, color: 'var(--pos)', fontWeight: 600 }}>Draft saved</span>}
              {!showMgrReject && (
                <button
                  className="btn-secondary"
                  onClick={() => setShowMgrReject(true)}
                  disabled={saving}
                  style={{ color: '#e74c3c', borderColor: '#e74c3c' }}
                >
                  Send Back to Employee
                </button>
              )}
            </div>

            {showMgrReject && (
              <div style={{ ...sectionCard, marginTop: 16, borderColor: '#e74c3c40', background: '#e74c3c0a' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#e74c3c' }}>
                  Send back to employee for edits
                </div>
                <textarea
                  className="form-input"
                  rows={3}
                  maxLength={10000}
                  placeholder="Explain what needs to change before resubmitting…"
                  value={mgrRejectReason}
                  onChange={e => setMgrRejectReason(e.target.value.slice(0, 10000))}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', marginBottom: 10 }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn-primary"
                    onClick={submitManagerReject}
                    disabled={saving || !mgrRejectReason.trim()}
                    style={{ background: '#e74c3c' }}
                  >
                    {saving ? 'Sending…' : 'Confirm Send Back'}
                  </button>
                  <button className="btn-secondary" onClick={() => { setShowMgrReject(false); setMgrRejectReason(''); }} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Final decision form */}
        {activeTab === 'final_decision' && isFinalApprover && a.status === 'pending_approver' && (
          <div>
            {a.employee_response && (
              <div style={{ ...sectionCard, marginBottom: 16, background: 'var(--bg-muted)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--text-primary)' }}>Employee's Reflection</div>
                <ResponseDisplay response={a.employee_response} type="employee" />
              </div>
            )}
            {a.manager_response && (
              <div style={{ ...sectionCard, marginBottom: 20, background: 'var(--bg-muted)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--text-primary)' }}>Manager's Feedback</div>
                <ResponseDisplay response={a.manager_response} type="manager" />
              </div>
            )}

            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Review all inputs and make the final decision for this appraisal ({REASON_LABELS[a.reason]}).
            </p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {[
                { value: 'approved', label: 'Approve', color: '#27ae60' },
                { value: 'rejected', label: 'Reject',  color: '#e74c3c' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFinalForm(f => ({ ...f, decision: opt.value, reject_target: opt.value === 'approved' ? '' : f.reject_target }))}
                  style={{
                    padding: '10px 28px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                    border: `2px solid ${finalForm.decision === opt.value ? opt.color : 'var(--border)'}`,
                    background: finalForm.decision === opt.value ? opt.color + '15' : 'var(--bg-input)',
                    color: finalForm.decision === opt.value ? opt.color : 'var(--text-primary)',
                    fontWeight: finalForm.decision === opt.value ? 700 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {finalForm.decision === 'rejected' && (
              <div style={{ marginBottom: 16 }}>
                <label style={formLabel}>Send Back To</label>
                <p style={formHint}>Choose whether this should go back to the manager for re-review, or to the employee to edit their reflection.</p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { value: 'manager',  label: 'Manager' },
                    { value: 'employee', label: 'Employee' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFinalForm(f => ({ ...f, reject_target: opt.value }))}
                      style={{
                        padding: '8px 20px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                        border: `2px solid ${finalForm.reject_target === opt.value ? '#e74c3c' : 'var(--border)'}`,
                        background: finalForm.reject_target === opt.value ? '#e74c3c15' : 'var(--bg-input)',
                        color: finalForm.reject_target === opt.value ? '#e74c3c' : 'var(--text-primary)',
                        fontWeight: finalForm.reject_target === opt.value ? 700 : 400,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={formLabel}>Decision Notes *</label>
              <textarea
                className="form-input"
                rows={3}
                maxLength={10000}
                placeholder="Reason for this decision, any conditions, next steps…"
                value={finalForm.decision_notes}
                onChange={e => setFinalForm(f => ({ ...f, decision_notes: e.target.value.slice(0, 10000) }))}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={saveFinalDecisionDraft} disabled={saving || draftSaving}>
                {draftSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn-primary"
                onClick={submitFinalDecision}
                disabled={saving || !finalForm.decision || !finalForm.decision_notes.trim() || (finalForm.decision === 'rejected' && !finalForm.reject_target)}
                style={{ background: finalForm.decision === 'rejected' ? '#e74c3c' : undefined }}
              >
                {saving ? 'Submitting…' : finalForm.decision === 'rejected' ? `Confirm — Send Back to ${finalForm.reject_target === 'manager' ? 'Manager' : finalForm.reject_target === 'employee' ? 'Employee' : '…'}` : 'Confirm Approval'}
              </button>
              {draftSaved && <span style={{ fontSize: 12, color: 'var(--pos)', fontWeight: 600 }}>Draft saved</span>}
            </div>
          </div>
        )}

        {/* Final view (read-only) */}
        {activeTab === 'final_view' && a.final_decision && (
          <DecisionOutcome decision={a.final_decision} />
        )}
      </div>
    </div>
  );
}

function fmtDT(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const EVENT_META = {
  created:                    { label: 'Appraisal Created',                        color: '#3498db' },
  employee_submitted:         { label: 'Employee Reflection Submitted',            color: '#3498db' },
  manager_submitted:          { label: 'Manager Review Submitted',                 color: '#9b59b6' },
  manager_rejected:           { label: 'Sent Back to Employee by Manager',         color: '#e74c3c' },
  final_approved:             { label: 'Final Decision — Approved',                color: '#27ae60' },
  final_rejected_to_manager:  { label: 'Sent Back to Manager by Final Approver',   color: '#e74c3c' },
  final_rejected_to_employee: { label: 'Sent Back to Employee by Final Approver',  color: '#e74c3c' },
};

const CURRENT_STAGE_META = {
  pending_employee: { label: 'Employee Reflection', color: '#f39c12' },
  pending_manager:  { label: 'Manager Review',       color: '#3498db' },
  pending_approver: { label: 'Final Approval',       color: '#3498db' },
};

function eventDetail(a, e) {
  if (e.event_type === 'created') {
    return `${REASON_LABELS[a.reason] || a.reason}${e.note ? ` — ${e.note}` : ''}`;
  }
  if (e.event_type === 'manager_submitted' && e.note) {
    return RECOMMENDATION_LABELS[e.note] || e.note;
  }
  return e.note || null;
}

// Full, append-only history — every round trip of a reject-and-resubmit
// loop shows as its own entry, since appraisal_events is never overwritten
// (unlike appraisals/appraisal_*_responses, which only hold the latest
// value per stage).
function buildEventTimelineSteps(a) {
  const steps = (a.events || []).map(e => ({
    key: `ev-${e.id}`,
    label: EVENT_META[e.event_type]?.label || e.event_type,
    at: e.created_at,
    by: e.actor ? userName(e.actor) : null,
    detail: eventDetail(a, e),
    color: EVENT_META[e.event_type]?.color || 'var(--text-muted)',
    done: true,
    current: false,
  }));

  const stage = CURRENT_STAGE_META[a.status];
  if (stage) {
    steps.push({
      key: 'current-stage',
      label: stage.label,
      at: null,
      by: null,
      detail: null,
      color: stage.color,
      done: false,
      current: true,
    });
  } else if (a.status === 'completed') {
    steps.push({
      key: 'completed',
      label: 'Completed',
      at: a.final_decided_at || a.manager_submitted_at,
      by: null,
      detail: 'Appraisal finalized',
      color: '#27ae60',
      done: true,
      current: false,
    });
  }

  return steps;
}

// Fallback for appraisals created before the appraisal_events log existed:
// derives one step per stage from the current-state columns. This can only
// ever show the LATEST pass through each stage, so a reject-and-resubmit
// loop collapses onto the same step instead of showing every round trip.
function buildTimelineSteps(a) {
  const managerRejectedCurrent = a.rejected_by_stage === 'manager' && a.status === 'pending_employee';
  const approverRejectedCurrent = a.rejected_by_stage === 'final_approver' && a.status === 'pending_employee';
  const approverRejectedToManagerCurrent = a.rejected_by_stage === 'final_approver' && a.status === 'pending_manager';

  const steps = [
    {
      key: 'created',
      label: 'Appraisal Created',
      at: a.created_at,
      by: userName(a.triggered_by),
      detail: `${REASON_LABELS[a.reason] || a.reason}${a.reason_notes ? ` — ${a.reason_notes}` : ''}`,
      color: '#3498db',
      done: true,
      current: false,
    },
    {
      key: 'employee',
      label: 'Employee Reflection',
      at: a.employee_submitted_at,
      by: a.employee_submitted_at ? userName(a.employee) : null,
      detail: (managerRejectedCurrent || approverRejectedCurrent)
        ? `Sent back by ${approverRejectedCurrent ? 'the Final Approver' : 'the Manager'} — awaiting resubmission: ${a.rejection_reason || ''}`
        : null,
      color: (managerRejectedCurrent || approverRejectedCurrent) ? '#e74c3c' : '#3498db',
      done: !!a.employee_submitted_at,
      current: a.status === 'pending_employee',
    },
    {
      key: 'manager',
      label: 'Manager Review',
      at: a.manager_submitted_at,
      by: a.manager_response?.submitted_by ? userName(a.manager_response.submitted_by) : null,
      detail: approverRejectedToManagerCurrent
        ? `Sent back by the Final Approver — awaiting re-review: ${a.rejection_reason || ''}`
        : managerRejectedCurrent
          ? `Sent back to employee: ${a.rejection_reason || ''}`
          : (a.manager_response?.recommendation ? RECOMMENDATION_LABELS[a.manager_response.recommendation] : null),
      color: (managerRejectedCurrent || approverRejectedToManagerCurrent) ? '#e74c3c' : '#9b59b6',
      done: !!a.manager_submitted_at,
      current: a.status === 'pending_manager',
    },
  ];

  steps.push({
    key: 'approver',
    label: 'Final Approval',
    at: a.final_decided_at,
    by: a.final_decision?.submitted_by ? userName(a.final_decision.submitted_by) : null,
    detail: a.final_decision?.decision_notes || null,
    color: approverRejectedCurrent || a.final_decision?.decision === 'rejected' ? '#e74c3c' : '#9b59b6',
    done: !!a.final_decided_at,
    current: a.status === 'pending_approver',
  });

  steps.push({
    key: 'completed',
    label: 'Completed',
    at: a.status === 'completed' ? (a.final_decided_at || a.manager_submitted_at) : null,
    by: null,
    detail: a.status === 'completed' ? 'Appraisal finalized' : null,
    color: '#27ae60',
    done: a.status === 'completed',
    current: false,
  });

  return steps;
}

function AppraisalTimeline({ a }) {
  const steps = (a.events && a.events.length) ? buildEventTimelineSteps(a) : buildTimelineSteps(a);
  return (
    <div style={{ padding: '4px 0 8px 4px' }}>
      {steps.map((s, i) => (
        <div key={s.key} style={{ display: 'flex', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16 }}>
            <div style={{
              width: 14, height: 14, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
              background: s.done ? s.color : 'var(--bg-modal)',
              border: s.done ? 'none' : `2px solid ${s.current ? s.color : 'var(--border)'}`,
              boxShadow: s.current && !s.done ? `0 0 0 3px ${s.color}22` : 'none',
            }} />
            {i < steps.length - 1 && (
              <div style={{ width: 2, flex: 1, minHeight: 40, background: s.done ? s.color : 'var(--border)', opacity: s.done ? 0.35 : 1 }} />
            )}
          </div>
          <div style={{ paddingBottom: 28, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: s.done || s.current ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {s.label}
              </span>
              {s.current && (
                <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>
                  {s.done ? 'Awaiting resubmission' : 'In progress'}
                </span>
              )}
              {!s.current && s.at && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDT(s.at)}</span>}
              {!s.current && !s.done && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Pending</span>}
            </div>
            {s.by && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>by {s.by}</div>}
            {s.detail && (
              <div style={{ fontSize: 12, color: s.color === '#e74c3c' ? '#e74c3c' : 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5, fontWeight: s.color === '#e74c3c' ? 500 : 400 }}>
                {s.detail}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function DecisionOutcome({ decision }) {
  const approved = decision.decision === 'approved';
  const color = approved ? '#27ae60' : '#e74c3c';
  return (
    <div style={{ textAlign: 'center', padding: '36px 0' }}>
      <div style={{
        width: 68, height: 68, borderRadius: '50%', margin: '0 auto 16px',
        background: color + '15', border: `2px solid ${color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          {approved ? (
            <path d="M5 12.5l4.5 4.5L19 7.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <>
              <path d="M7 7l10 10" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
              <path d="M17 7L7 17" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
            </>
          )}
        </svg>
      </div>
      <div style={{
        display: 'inline-block', padding: '3px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700,
        color, background: color + '15', marginBottom: 14, letterSpacing: '0.3px',
      }}>
        {approved ? 'Approved' : 'Rejected'}
      </div>
      {decision.decision_notes && (
        <div style={{
          maxWidth: 440, margin: '0 auto', textAlign: 'left', ...sectionCard,
          borderColor: color + '30',
        }}>
          <div style={{ ...sectionHead, marginBottom: 6 }}>Decision Notes</div>
          <p style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{decision.decision_notes}</p>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{value || '—'}</div>
    </div>
  );
}

function ResponseDisplay({ response, type, full }) {
  if (type === 'employee') {
    const fields = [
      { key: 'things_went_well',     label: 'Things That Went Well' },
      { key: 'could_be_better',      label: 'Could Be Better' },
      { key: 'next_term_aspirations',label: 'Next Term Aspirations' },
      { key: 'support_required',     label: 'Support Required' },
    ];
    return (
      <div style={{ display: 'grid', gridTemplateColumns: full ? '1fr 1fr' : '1fr', gap: 12 }}>
        {fields.map(({ key, label }) => (
          response[key] ? (
            <div key={key}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{response[key]}</div>
            </div>
          ) : null
        ))}
      </div>
    );
  }

  if (type === 'manager') {
    const recColor = { recommend: '#27ae60', not_recommend: '#e74c3c', conditional: '#f39c12' };
    return (
      <div>
        {[
          { key: 'goals_feedback',       label: 'Goals Assessment' },
          { key: 'skills_feedback',      label: 'Skills Assessment' },
          { key: 'values_feedback',      label: 'Values Assessment' },
          { key: 'potential_assessment', label: 'Potential & Growth' },
        ].map(({ key, label }) => (
          response[key] ? (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{response[key]}</div>
            </div>
          ) : null
        ))}
        {response.recommendation && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
              background: (recColor[response.recommendation] || '#888') + '20',
              color: recColor[response.recommendation] || '#888',
            }}>
              {RECOMMENDATION_LABELS[response.recommendation]}
            </span>
            {response.recommendation_notes && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{response.recommendation_notes}</span>
            )}
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ─── Create appraisal modal ───────────────────────────────────────────────────

export function CreateAppraisalModal({ onClose, onCreated }) {
  const { appId } = useParams();
  const api = useApi();
  const [form, setForm] = useState({
    platform_user_id: '',
    role_id: '',
    reason: '',
    reason_notes: '',
    included_cycle_ids: [],
  });
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.listRoleAssignments(appId),
      api.roles.list(appId),
      api.listReviewCycles(appId),
    ]).then(([assignments, rolesData, cyclesData]) => {
      setRoles(rolesData.items || []);
      setCycles(cyclesData.items || []);
      const rawAssignments = assignments.items || [];
      setEmployees(rawAssignments);
    }).catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  }, [appId]);

  // Only active cycles can be included on a new appraisal — closed/planned
  // ones aren't open for review submissions yet.
  const activeCycles = cycles.filter(c => c.status === 'active');

  // Get unique users from role assignments
  const uniqueUsers = [...new Map(
    employees.map(e => [e.platform_user_id, { platform_user_id: e.platform_user_id, user: e.user }])
  ).values()];

  // Roles for selected user
  const userRoles = employees.filter(e => String(e.platform_user_id) === String(form.platform_user_id));

  const toggleCycle = id => {
    setForm(f => ({
      ...f,
      included_cycle_ids: f.included_cycle_ids.includes(id)
        ? f.included_cycle_ids.filter(c => c !== id)
        : [...f.included_cycle_ids, id],
    }));
  };

  const allCyclesSelected = activeCycles.length > 0 && activeCycles.every(c => form.included_cycle_ids.includes(c.id));
  const toggleAllCycles = () => {
    setForm(f => ({
      ...f,
      included_cycle_ids: allCyclesSelected ? [] : activeCycles.map(c => c.id),
    }));
  };

  const handleSubmit = async () => {
    if (!form.platform_user_id || !form.role_id || !form.reason || !form.included_cycle_ids.length) {
      setError('Please fill in all required fields and select at least one review period');
      return;
    }
    setSaving(true); setError('');
    try {
      await api.createAppraisal(appId, {
        ...form,
        platform_user_id: parseInt(form.platform_user_id),
        role_id: parseInt(form.role_id),
        included_cycle_ids: form.included_cycle_ids,
      });
      onCreated();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create appraisal');
    } finally { setSaving(false); }
  };

  const userName2 = (u) => {
    if (!u) return `User #${u}`;
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User`;
  };

  return (
    <div style={modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div data-dropdown-bounds style={{ ...modalBox, maxWidth: 540 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>Trigger New Appraisal</h2>
          <button className="btn-link-sm" onClick={onClose} style={{ fontSize: 18 }}>✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

        {loading ? <div className="loading">Loading…</div> : (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={formLabel}>Employee *</label>
              <SearchableSelect
                placeholder="— Select employee —"
                btnClass="form-select-btn"
                matchTriggerWidth
                showAll={false}
                value={form.platform_user_id}
                onChange={uid => {
                  const firstRole = employees.find(em => String(em.platform_user_id) === String(uid));
                  setForm(f => ({ ...f, platform_user_id: uid, role_id: firstRole ? String(firstRole.role_id) : '' }));
                }}
                options={uniqueUsers.map(u => ({ value: String(u.platform_user_id), label: userName2(u.user) }))}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={formLabel}>Role *</label>
              <SearchableSelect
                placeholder="— Select role —"
                btnClass="form-select-btn"
                matchTriggerWidth
                showAll={false}
                value={form.role_id}
                onChange={val => setForm(f => ({ ...f, role_id: val }))}
                options={userRoles.map(ra => ({ value: String(ra.role_id), label: ra.role?.title || `Role #${ra.role_id}` }))}
                disabled={!form.platform_user_id}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={formLabel}>Reason *</label>
              <SearchableSelect
                placeholder="— Select reason —"
                btnClass="form-select-btn"
                matchTriggerWidth
                showAll={false}
                value={form.reason}
                onChange={val => setForm(f => ({ ...f, reason: val }))}
                options={Object.entries(REASON_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={formLabel}>Additional Notes</label>
              <input
                className="form-input"
                maxLength={10000}
                value={form.reason_notes}
                onChange={e => setForm(f => ({ ...f, reason_notes: e.target.value.slice(0, 10000) }))}
                placeholder="e.g. 6-month salary review, exceptional performance..."
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={formLabel}>Review Periods to Include * <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(select all applicable cycles)</span></label>
              {activeCycles.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--warn)' }}>No active review cycles available.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', background: 'var(--bg-input)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
                    <input
                      type="checkbox"
                      checked={allCyclesSelected}
                      onChange={toggleAllCycles}
                    />
                    Select All
                  </label>
                  {activeCycles.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={form.included_cycle_ids.includes(c.id)}
                        onChange={() => toggleCycle(c.id)}
                      />
                      {c.cycle_name} ({c.year}) — {c.periodicity}
                      <span style={{ marginLeft: 4 }}><StatusBadge status={c.status || 'planned'} /></span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Creating…' : 'Create Appraisal'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Appraisal Dashboard ─────────────────────────────────────────────────

export default function AppraisalDashboard({ onCountReady }) {
  const { appId } = useParams();
  const api = useApi();
  const { canManage, canWrite } = useWorkspace();

  const [myAppraisals, setMyAppraisals]       = useState([]);
  const [manageAppraisals, setManageAppraisals] = useState([]);
  const [approverAppraisals, setApproverAppraisals] = useState([]);
  const [allAppraisals, setAllAppraisals]     = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');
  const [activeView, setActiveView]           = useState('mine');
  const [showCreate, setShowCreate]           = useState(false);
  const [selectedId, setSelectedId]           = useState(null);

  const isAdmin = canManage || canWrite;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [mine, manage, approver] = await Promise.all([
        api.listAppraisals(appId, { view: 'mine' }),
        api.listAppraisals(appId, { view: 'manage' }),
        api.listAppraisals(appId, { view: 'pending_approver' }),
      ]);
      setMyAppraisals(mine.items || []);
      setManageAppraisals(manage.items || []);
      setApproverAppraisals(approver.items || []);

      if (isAdmin) {
        const all = await api.listAppraisals(appId, { view: 'all' });
        setAllAppraisals(all.items || []);
      }

      // Bubble up pending count
      const pendingCount = (mine.items || []).filter(a => a.status === 'pending_employee').length
        + (manage.items || []).length
        + (approver.items || []).length;
      onCountReady?.(pendingCount);
    } catch {
      setError('Failed to load appraisals');
    } finally {
      setLoading(false);
    }
  }, [appId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const views = [
    { key: 'mine',     label: 'My Appraisals',   count: myAppraisals.length },
    ...(manageAppraisals.length  ? [{ key: 'manage',   label: 'Team Appraisals',  count: manageAppraisals.length }]  : []),
    ...(approverAppraisals.length? [{ key: 'approver', label: 'Pending My Decision', count: approverAppraisals.length }] : []),
    ...(isAdmin                  ? [{ key: 'all',      label: 'All Appraisals',   count: allAppraisals.length }]     : []),
  ];

  const currentList = {
    mine:     myAppraisals,
    manage:   manageAppraisals,
    approver: approverAppraisals,
    all:      allAppraisals,
  }[activeView] || [];

  if (loading) return <div className="loading">Loading appraisals…</div>;

  return (
    <div>
      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {/* View tabs */}
        <div style={{ display: 'flex', gap: 6 }}>
          {views.map(v => (
            <button
              key={v.key}
              onClick={() => setActiveView(v.key)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                background: activeView === v.key ? 'var(--accent)' : 'var(--bg-muted)',
                color: activeView === v.key ? '#fff' : 'var(--text-secondary)',
                border: 'none', fontWeight: activeView === v.key ? 600 : 400,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {v.label}
              {v.count > 0 && (
                <span style={{
                  background: activeView === v.key ? 'rgba(255,255,255,0.3)' : '#e74c3c',
                  color: activeView === v.key ? '#fff' : '#fff',
                  borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                }}>
                  {v.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {isAdmin && (
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={() => setShowCreate(true)}>
            + Trigger Appraisal
          </button>
        )}
      </div>

      {currentList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 14 }}>No appraisals found in this view.</div>
          {activeView === 'mine' && (
            <p style={{ fontSize: 12, marginTop: 6 }}>Appraisals are triggered by your HR team or app owner.</p>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {currentList.map(a => (
            <AppraisalCard key={a.id} appraisal={a} onOpen={() => setSelectedId(a.id)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAppraisalModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {selectedId && (
        <AppraisalDetail
          appraisalId={selectedId}
          onClose={() => setSelectedId(null)}
          onRefresh={load}
          canManage={canManage}
        />
      )}
    </div>
  );
}

function AppraisalCard({ appraisal: a, onOpen }) {
  const statusColor = STATUS_COLORS[a.status] || '#888';
  return (
    <div
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
        padding: '14px 18px', cursor: 'pointer', transition: 'box-shadow 0.15s',
        display: 'flex', alignItems: 'center', gap: 16,
      }}
      onClick={onOpen}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: statusColor + '20',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, flexShrink: 0,
      }}>
        {a.status === 'completed' ? '✓' : a.status === 'rejected' ? '✕' : '⏳'}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 2 }}>
          {userName(a.employee)} — {REASON_LABELS[a.reason] || a.reason}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {a.role?.title}
          {a.included_cycles?.length ? ` · ${a.included_cycles.length} review period${a.included_cycles.length > 1 ? 's' : ''}` : ''}
          {a.reason_notes ? ` · ${a.reason_notes}` : ''}
        </div>
      </div>

      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <StatusBadge status={a.status} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {new Date(a.created_at).toLocaleDateString()}
        </div>
      </div>

      <div style={{ color: 'var(--text-muted)', fontSize: 16, flexShrink: 0 }}>›</div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 20,
};

const modalBox = {
  background: 'var(--bg-modal)', color: 'var(--text-primary)',
  borderRadius: 12, padding: '28px 32px',
  width: '100%', maxWidth: 700, boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
};

const sectionCard = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px',
  background: 'var(--bg-card)',
};

const sectionHead = {
  fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase',
  letterSpacing: '0.5px', marginBottom: 10,
};

const formLabel = {
  display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4,
};

const formHint = {
  fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 6px',
};

const summaryBox = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px',
  marginBottom: 10, background: 'var(--bg-card)',
};
